import pako from 'pako'
import classnames from 'classnames/bind'
import { useEffect, useRef, ReactNode } from 'react'

// Libs
import { useAppDispatch } from '@/redux/hooks'
import { isArray } from 'lodash-es'
import { toast } from 'react-toastify'
import { resetChatData } from '@/redux/reducers/socket'
import { useAppSelector } from '@/redux/hooks'
import { getTime } from 'date-fns'

// Components
import Player from './components/Player'
import { WS_EVENT_CHAT, WS_EVENT_ROOM } from '@/constants/socketEvent'
import { handleRecv } from '@/methods/message/handleMessageReceive'

// Constants

// Styles
import styles from './style.module.scss'

const cx = classnames.bind(styles)

interface PlaybackProps {
  className?: string
  info: any
  eventLog: any
  currentTime: any
  playback: any
  bagComponent?: ReactNode
}

const fetchEventLogs = (url: string, responseType: XMLHttpRequestResponseType = 'arraybuffer') => {
  if (!url) {
    return Promise.reject('url is empty')
  }
  if (!url.includes('://')) {
    url = `https://${url}`
  }
  return new Promise((res, rej) => {
    const xhr = new XMLHttpRequest()
    xhr.open('get', url, true)
    xhr.responseType = responseType

    xhr.onload = function () {
      res(xhr.response)
    }
    xhr.onreadystatechange = function () {
      if (xhr.readyState === XMLHttpRequest.DONE && xhr.status === 404) {
        rej(xhr)
      }
    }
    xhr.onerror = function (error) {
      rej(error)
    }
    xhr.send(null)
  })
}

const Playback = (props: PlaybackProps) => {
  const { className, info, eventLog, bagComponent } = props

  const roomData = useAppSelector((state) => state.room?.roomData)

  const chats = useRef<any>({})
  const totalTime = useRef<any>(0)
  const currentTime = useRef<any>(0)
  const playbackInterval = useRef<any>(null)

  const dispatch = useAppDispatch()

  const getSwitchTimeInfo = (points: any, type: any) => {
    return points.find((item: any) => {
      if (item.sid === WS_EVENT_ROOM.sid && item.ssid === WS_EVENT_ROOM.switch) {
        let body = item.body
        if (typeof body === 'string') {
          try {
            body = JSON.parse(body)
          } catch (e) {
            console.log(e)
          }
        }
        if (body[0].key === 'switch' && body[0].value === type) {
          return true
        }
      }
      return false
    })
  }

  const parseLogs = (points: any, startTimestamp: any) => {
    for (let i = 0, len = points.length; i < len; i++) {
      const item = points[i]
      const time = (item.ts - startTimestamp) * 1000
      if (item.ts >= startTimestamp && item.sid === WS_EVENT_CHAT.sid && item.ssid === WS_EVENT_CHAT.GROUP) {
        chats.current[time] = item
      } else if (item.ts >= startTimestamp && item.sid === WS_EVENT_CHAT.sid && item.ssid === WS_EVENT_CHAT.REWARD) {
        chats.current[time] = item
      }
    }
  }

  const handleChats = (chats: any[]) => {
    chats?.forEach((msg) => {
      const actions = handleRecv(msg)
      if (actions) {
        if (isArray(actions)) {
          actions.forEach((a: any) => {
            if (typeof a !== 'undefined') {
              try {
                dispatch(a)
              } catch (e) {
                console.error('shop', 'dispatch error', e)
              }
            }
          })
        } else {
          dispatch(actions)
        }
      }
    })
  }

  const deleteAllChats = () => {
    dispatch(resetChatData())
  }

  const getSnap = (offsetTime: any, currentTime: any) => {
    const seek = Math.abs(offsetTime - currentTime) > 1000 //快進快退
    const chat = seek ? [] : chats.current[offsetTime] ? [chats.current[offsetTime]] : []

    if (seek) {
      for (const chatKey in chats.current) {
        if (chatKey <= offsetTime) {
          chat.push(chats.current[chatKey])
        }
      }
    }

    return {
      chat,
    }
  }

  const setSnapshot = (time: any) => {
    const snapshot = getSnap(time, currentTime.current)
    if (Math.abs(time - currentTime.current) > 1000) {
      deleteAllChats()
    }
    currentTime.current = time

    handleChats(snapshot.chat)
  }

  const playbackBegin = (ts: any) => {
    let time = ts
    setSnapshot(time)
    const interval = 100
    playbackInterval.current = setInterval(() => {
      time += interval
      setSnapshot(time)
      //console.log('totalTime ===>', totalTime.current, time)
      if (time > totalTime.current) {
        playbackInterval.current && clearInterval(playbackInterval.current)
      }
    }, interval)
  }

  const handleTimeChange = (time: any, playStatus: number) => {
    time = Math.floor(time / 500) * 500
    playbackInterval.current && clearInterval(playbackInterval.current)
    if (playStatus === 1) {
      playbackBegin(time)
    } else {
      setSnapshot(time)
    }
  }

  useEffect(() => {
    if (eventLog && eventLog.length > 0) {
      fetchEventLogs(eventLog[0])
        .then((res: any) => {
          const compressed = new Uint8Array(res)
          const result = pako.inflate(compressed)
          const pointsStr = new TextDecoder('UTF-8').decode(result)
          const points = JSON.parse(pointsStr)
          if (points.events.length === 0) {
            throw new Error('no data')
          }
          const resetPoints = points.events.sort((x: any, y: any) => x.ts - y.ts)
          //const startTimeInfo = getSwitchTimeInfo(resetPoints, 1)
          const endTimeInfo = getSwitchTimeInfo(resetPoints, -1)
          //const startTime = startTimeInfo ? startTimeInfo.ts : resetPoints[0].ts
          const startTime = getTime(new Date(roomData.liveTime ? roomData.liveTime : roomData.startTime)) / 1000
          const endTime = endTimeInfo ? endTimeInfo.ts : resetPoints[resetPoints.length - 1].ts
          const formatPoints = resetPoints
          parseLogs(formatPoints, startTime)

          //handleChats(chats)
          //console.log('===> chats', chats.current)

          totalTime.current = (endTime - startTime) * 1000
        })
        .catch((e) => {
          console.error(`請求異常${e}, 請檢查EventLogs是否正常`)
          toast.error(`請檢查EventLog`)
        })
    }

    return () => {
      playbackInterval.current && clearInterval(playbackInterval.current)
      chats.current = {}
      deleteAllChats()
    }
  }, [eventLog])

  const playerProps = {
    playbackBegin,
    onTimeChange: handleTimeChange,
    bagComponent,
  }

  return (
    <div className={cx('playback', className)}>
      <Player videoList={info[0].data} {...playerProps} />
    </div>
  )
}

export default Playback
